import { observable, action } from 'mobx';
import { sendCommandToDefault } from '../socket';

type Initial = {
  type: 'initial';
};

type Loading = {
  type: 'loading';
};

type Loaded<T> = {
  type: 'loaded';
  data: T;
};

type Errored = {
  type: 'errored';
  error: Error;
};

type DataSTM<T> = Initial | Loading | Loaded<T> | Errored;

export class ServiceStore {
  @observable servicesData: DataSTM<ServiceInfo[]> = { type: 'initial' };

  @action.bound
  initServicesData() {
    switch (this.servicesData.type) {
      case 'loading':
      case 'loaded':
      case 'errored':
        return;
      case 'initial':
        this._loadServicesData();
        return;
    }

    const _exhaustiveCheck: never = this.servicesData;
  }

  @action.bound
  reloadServicesData() {
    switch (this.servicesData.type) {
      case 'loading':
      case 'loaded':
      case 'errored':
      case 'initial':
        this._loadServicesData();
        return;
    }

    const _exhaustiveCheck: never = this.servicesData;
  }

  @action.bound
  updateServices(services: ServiceInfo[]) {
    this.servicesData = { type: 'loaded', data: services };
  }

  @action.bound
  updateWithPatches(servicePatches: ServiceInfoPatch[]) {
    switch (this.servicesData.type) {
      case 'loaded':
        const updatesServiceInfo = this.servicesData.data.map(serviceInfo => {
          const servicePatch = servicePatches.find(
            patch => patch.serviceName === serviceInfo.serviceName,
          );
          if (servicePatch) {
            return { ...serviceInfo, ...servicePatch };
          } else {
            return serviceInfo;
          }
        });

        this.servicesData = {
          type: 'loaded',
          data: updatesServiceInfo,
        };

        return;
    }
  }

  @action.bound
  updateService(newServiceInfo: ServiceInfo) {
    this.updateServiceInfoByServiceName(
      newServiceInfo.serviceName,
      _oldServiceInfo => newServiceInfo,
    );
  }

  @action.bound
  markServiceWithActionInProgress(
    serviceName: string,
    actionInProgress: ServiceInfo['actionInProgress'],
  ) {
    this.updateServiceInfoByServiceName(serviceName, serviceInfo => ({
      ...serviceInfo,
      actionInProgress,
    }));
  }

  @action.bound
  resetActionInProgress(serviceName: string) {
    this.updateServiceInfoByServiceName(serviceName, serviceInfo => ({
      ...serviceInfo,
      actionInProgress: undefined,
    }));
  }

  @action
  private updateServiceInfoByServiceName(
    serviceName: string,
    updater: (serviceInfo: ServiceInfo) => ServiceInfo,
  ) {
    switch (this.servicesData.type) {
      case 'loaded':
        this.servicesData = {
          type: 'loaded',
          data: this.servicesData.data.map(
            serviceInfo =>
              serviceInfo.serviceName === serviceName
                ? updater(serviceInfo)
                : serviceInfo,
          ),
        };
    }
  }

  private _loadServicesData() {
    return sendCommandToDefault('requestserviceinformation')
      .then(() => (this.servicesData = { type: 'loading' }))
      .catch(error => (this.servicesData = { type: 'errored', error }));
  }
}

export const serviceStore = new ServiceStore();
